import { GroupListItem } from '@/api/types'
import { useGroupActions } from '@/hooks/useGroupActions'
import { useGroupStore } from '@/stores/useGroupStore'
import { useTabAuth } from '@/hooks/useTabAuth'
import { useEffect } from 'react'
import GroupEditPanel from './GroupEditPanel'
import GroupMembersPanel from './GroupMembersPanel'
import ToastUtils from '@/utils/toast-utils'
import { logPrint } from '@/utils/my-utils'
import { useChatActions } from '@/hooks/useChatActions'
import MessageManager from '@/utils/ably-im/message-manager'
import { usePageDataStore } from '@/stores/usePageDataStore'

interface GroupDetailProps {
  group: GroupListItem
  onNavigateToGroupChat?: (conversationId: string) => Promise<void>
  onGroupRemoved?: () => void
}

export enum GroupDetailsPanelType {
  DETAIL = 'detail',
  EDIT = 'edit',
  MEMBERS = 'members',
  SETTINGS = 'settings'
}

export default function GroupDetail({ group, onNavigateToGroupChat, onGroupRemoved }: GroupDetailProps) {
  const groupActions = useGroupActions()
  const { removeGroupFromList } = useGroupStore()
  const tabAuth = useTabAuth()
  const { deleteConversationHistory } = useChatActions()

  const groupDetailsPanelType = usePageDataStore(state => state.groupDetailsPanelType)
  const setGroupDetailsPanelType = usePageDataStore(state => state.setGroupDetailsPanelType)

  // 获取当前用户信息
  const currentUser = tabAuth.getUserInfo()

  // 判断当前用户是否为群主
  const isGroupOwner = currentUser?.id === group.owner_id

  // 处理退出群组
  const handleLeaveGroup = async () => {
    const confirmed = confirm(`确定要退出群组 ${group.name} 吗？`)
    if (!confirmed) return

    ToastUtils.showLoading(true, '退出群组中...');
    const success = await groupActions.leaveGroup(group.id)
    if (success) {
      removeGroupFromList(group.id)
      onGroupRemoved?.()
      ToastUtils.showToastSuccess('退出群组成功');

      MessageManager.getInstance().sendGroupMemberLeaveNotification(group.conversation_id);
    }
    ToastUtils.showLoading(false);
  }

  // 处理解散群组
  const handleDisbandGroup = async () => {
    if (!isGroupOwner) return;

    const confirmed = confirm(`确定要解散群组 ${group.name} 吗？\n解散后群组将被永久删除，所有成员都将被移除。`)
    if (!confirmed) return

    ToastUtils.showLoading(true, '解散群组中...');
    const success = await groupActions.disbandGroup(group.id)
    if (success) {
      removeGroupFromList(group.id)
      onGroupRemoved?.()
      ToastUtils.showToastSuccess('解散群组成功');

      MessageManager.getInstance().sendGroupDissolveNotification(group.conversation_id);
    }
    ToastUtils.showLoading(false);
  }

  useEffect(() => {
    logPrint("group useEffect ： ", group)
    groupActions.clearError();

    groupActions.getGroupInfo(group.id)

    // 当群组变化时，重置到详情面板
    setGroupDetailsPanelType(GroupDetailsPanelType.DETAIL);
  }, [group])

  // 处理面板切换
  const handlePanelChange = (panel: GroupDetailsPanelType) => {
    setGroupDetailsPanelType(panel);
  };

  // 处理返回详情页
  const handleBackToDetail = () => {
    setGroupDetailsPanelType(GroupDetailsPanelType.DETAIL);
  };

  // 处理清除聊天记录
  const handleDeleteConversationHistory = async () => {
    const confirmed = confirm(`确定要清除聊天记录吗？`)
    if (!confirmed) return

    const success = await deleteConversationHistory(group.conversation_id)
    if (success) {
      ToastUtils.showToastSuccess('清除聊天记录成功');
    } else {
      ToastUtils.showToastFail('清除聊天记录失败');
    }
  }

  // 处理开始群聊
  const handleStartGroupChat = async () => {
    if (onNavigateToGroupChat) {
      await onNavigateToGroupChat(group.conversation_id)
    }
  }

  // 根据当前面板渲染不同的内容
  const renderPanel = () => {
    switch (groupDetailsPanelType) {
      case GroupDetailsPanelType.EDIT:
        return <GroupEditPanel group={group} onBack={handleBackToDetail} />;
      case GroupDetailsPanelType.MEMBERS:
        return <GroupMembersPanel group={group} onBack={handleBackToDetail} />;
      case GroupDetailsPanelType.SETTINGS:
        return <GroupEditPanel group={group} onBack={handleBackToDetail} />; // 设置也使用编辑面板
      default:
        return <></>;
    }
  };

  // 渲染详情面板
  const renderDetailPanel = () => (
    <div className="h-full flex flex-col bg-white">
      {/* 头像和基本信息 */}
      <div className="flex flex-col items-center py-8 px-6">
        <div className="relative mb-4">
          <img
            src={group.avatar || `https://via.placeholder.com/96/9333ea/white?text=${group.name[0]?.toUpperCase() || '?'}`}
            alt={group.name}
            className="w-24 h-24 rounded-full"
          />
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {group.name}
        </h2>

        {/* <p className="text-sm text-gray-500 mb-4">
          ID: {group.id}
        </p> */}

        {/* 类型标识 */}
        <div className="px-3 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
          群组
        </div>
      </div>

      {/* 操作按钮区域 */}
      <div className="px-6 space-y-4">
        {/* 编辑群组 */}
        {
          isGroupOwner && (
            <div
              className="flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
              onClick={() => handlePanelChange(GroupDetailsPanelType.EDIT)}
            >
              <span className="text-sm text-gray-700">编辑群组</span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )
        }

        {/* 群组成员 */}
        {
          isGroupOwner && (
            <div
              className="flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
              onClick={() => handlePanelChange(GroupDetailsPanelType.MEMBERS)}
            >
              <span className="text-sm text-gray-700">群组成员</span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )
        }

        {/* 群组设置 */}
        {/* <div
          className="flex items-center justify-between py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
          onClick={() => handlePanelChange(GroupDetailsPanelType.SETTINGS)}
        >
          <span className="text-sm text-gray-700">群组设置</span>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div> */}

        {/* 清除聊天记录 */}
        {/* <div
          className="py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
          onClick={handleDeleteConversationHistory}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">清除聊天记录</span>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div> */}

        {/* 消息通知 */}
        {/* <div className="py-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-700">消息通知</div>
              <div className="text-xs text-gray-500">
                关闭后将不会收到群组消息通知
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                defaultChecked={true}
                disabled={groupActions.isLoading}
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div> */}

        {/* 群主操作：解散群组 */}
        {isGroupOwner ? (
          <div
            className="py-3 border-b border-gray-100 cursor-pointer hover:bg-red-50"
            onClick={handleDisbandGroup}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-red-600">解散群组</span>
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
          </div>
        ) : (
          /* 普通成员操作：退出群组 */
          <div
            className="py-3 border-b border-gray-100 cursor-pointer hover:bg-red-50"
            onClick={handleLeaveGroup}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-red-600">退出群组</span>
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
          </div>
        )}

        {/* 错误提示 */}
        {groupActions.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{groupActions.error}</p>
          </div>
        )}
      </div>

      {/* 底部聊天按钮 */}
      <div className="mt-auto p-6">
        <button
          onClick={handleStartGroupChat}
          className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          disabled={groupActions.isLoading || !onNavigateToGroupChat}
        >
          {groupActions.isLoading ? '处理中...' : '开始群聊'}
        </button>
      </div>
    </div>
  );

  return (
    <div className='flex flex-row w-full h-full'>
      <div className='flex-1 h-full'>
        {renderDetailPanel()}
      </div>

      {/* 竖条分割线 */}
      <div className="w-px bg-gray-200 h-full"></div>

      <div className={`flex-1 ${groupDetailsPanelType === GroupDetailsPanelType.DETAIL ? 'hidden' : ''}`}>
        {renderPanel()}
      </div>
    </div>
  );
}
